import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import usePushNotifications from '@/hooks/usePushNotifications';

const STORAGE_KEY = 'hakken-permissions-seen';

function ToggleRow({ icon, title, subtitle, enabled, loading, onClick, status }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading || status === 'denied'}
      className="flex w-full items-center gap-4 rounded-2xl p-4 text-left transition-all active:scale-[0.98]"
      style={{
        background: enabled
          ? 'linear-gradient(135deg, rgb(var(--hc-accent) / 0.12), rgb(var(--hc-accent) / 0.06))'
          : 'rgba(255,255,255,0.04)',
        border: enabled
          ? '1.5px solid rgb(var(--hc-accent) / 0.4)'
          : '1.5px solid rgba(255,255,255,0.1)',
      }}
    >
      {/* Icon */}
      <div
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-2xl"
        style={{
          background: enabled ? 'rgb(var(--hc-accent) / 0.18)' : 'rgba(255,255,255,0.08)',
          border: enabled ? '1.5px solid rgb(var(--hc-accent) / 0.5)' : '1.5px solid rgba(255,255,255,0.1)',
        }}
      >
        {icon}
      </div>

      {/* Text */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-bold text-fg text-sm">{title}</span>
          {status === 'denied' && (
            <span className="rounded-full bg-red-500/20 px-2 py-0.5 text-[9px] font-black uppercase tracking-wide text-red-400">
              Bloqueado
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs leading-snug text-muted">{subtitle}</p>
      </div>

      {/* Toggle */}
      <div className="shrink-0">
        {loading ? (
          <div className="h-7 w-12 animate-pulse rounded-full bg-fg/20" />
        ) : status === 'denied' ? (
          <div
            className="flex h-7 w-12 items-center justify-center rounded-full"
            style={{ background: 'rgba(239,68,68,0.2)', border: '1.5px solid rgba(239,68,68,0.4)' }}
          >
            <span className="text-xs text-red-400">✕</span>
          </div>
        ) : (
          <motion.div
            className="relative h-7 w-12 rounded-full"
            animate={{ background: enabled ? 'rgb(var(--hc-accent))' : 'rgba(127,127,127,0.3)' }}
            transition={{ duration: 0.2 }}
            style={{ border: enabled ? '1.5px solid rgb(var(--hc-accent))' : '1.5px solid rgba(127,127,127,0.35)' }}
          >
            <motion.div
              className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-md"
              animate={{ left: enabled ? '26px' : '2px' }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            />
          </motion.div>
        )}
      </div>
    </button>
  );
}

export default function PermissionsModal({
  geoPermission,        // 'granted' | 'denied' | 'prompt' | 'loading'
  compassPermission,    // 'granted' | 'denied' | 'prompt' | 'unsupported'
  onRequestGeo,
  onRequestCompass,
  onClose,
}) {
  const [compassLoading, setCompassLoading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [visible, setVisible] = useState(true);
  const push = usePushNotifications();

  const geoGranted = geoPermission === 'granted';
  const compassGranted = compassPermission === 'granted';
  const compassUnsupported = compassPermission === 'unsupported';

  const handleRequestGeo = async () => {
    if (geoGranted) return;
    setGeoLoading(true);
    await onRequestGeo?.();
    setGeoLoading(false);
  };

  const handleRequestCompass = async () => {
    if (compassGranted || compassUnsupported) return;
    setCompassLoading(true);
    await onRequestCompass?.();
    setCompassLoading(false);
  };

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
    setTimeout(onClose, 300);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[150] flex items-end justify-center"
          style={{ background: 'rgba(8,8,26,0.85)', backdropFilter: 'blur(6px)' }}
          onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 22, stiffness: 220 }}
            className="w-full max-w-lg overflow-hidden rounded-t-3xl"
            style={{
              background: 'linear-gradient(180deg, rgb(var(--hc-surface)) 0%, rgb(var(--hc-bg)) 100%)',
              border: '1px solid rgb(var(--hc-accent) / 0.2)',
              borderBottom: 'none',
              paddingBottom: 'env(safe-area-inset-bottom, 16px)',
            }}
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="h-1 w-10 rounded-full bg-fg/20" />
            </div>

            <div className="px-5 pb-2 pt-2">
              {/* Header */}
              <div className="mb-5 flex items-start gap-4">
                <motion.div
                  className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-3xl"
                  style={{
                    background: 'linear-gradient(135deg, rgb(var(--hc-accent) / 0.2), rgb(var(--hc-accent) / 0.08))',
                    border: '1.5px solid rgb(var(--hc-accent) / 0.4)',
                  }}
                  animate={{ rotate: [0, -5, 5, 0] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                >
                  🗺️
                </motion.div>
                <div>
                  <h2 className="font-serif text-lg font-black text-fg">Permissões do mapa</h2>
                  <p className="mt-1 text-xs leading-relaxed text-muted">
                    Para uma melhor experiência, o HakkenCity precisa de acesso à tua localização e orientação do dispositivo.
                  </p>
                </div>
              </div>

              {/* Toggles */}
              <div className="flex flex-col gap-3 mb-5">
                <ToggleRow
                  icon="📍"
                  title="Localização"
                  subtitle="Necessário para ver os POIs à tua volta e fazer check-in nos locais."
                  enabled={geoGranted}
                  loading={geoLoading}
                  status={geoPermission === 'denied' ? 'denied' : undefined}
                  onClick={handleRequestGeo}
                />

                {!compassUnsupported && (
                  <ToggleRow
                    icon="🧭"
                    title="Movimento e Orientação"
                    subtitle="Roda o mapa conforme a direção do teu telemóvel. Opcional mas recomendado."
                    enabled={compassGranted}
                    loading={compassLoading}
                    status={compassPermission === 'denied' ? 'denied' : undefined}
                    onClick={handleRequestCompass}
                  />
                )}

                {push.supported && (
                  <ToggleRow
                    icon="🔔"
                    title="Notificações"
                    subtitle="Avisamos-te de missões concluídas, medalhas e ofertas perto de ti."
                    enabled={push.status === 'granted' && push.subscribed}
                    loading={push.loading}
                    status={push.status === 'denied' ? 'denied' : undefined}
                    onClick={() => {
                      if (push.status === 'granted' && push.subscribed) push.unsubscribe();
                      else push.subscribe();
                    }}
                  />
                )}
              </div>

              {/* Denied hint */}
              <AnimatePresence>
                {(geoPermission === 'denied' || compassPermission === 'denied') && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-4 overflow-hidden rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3"
                  >
                    <p className="text-xs font-bold leading-relaxed text-red-300">
                      ⚠️ Permissão bloqueada. Vai às definições do teu telemóvel e ativa o acesso para o HakkenCity.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* CTA */}
              <button
                type="button"
                onClick={handleClose}
                className="btn-gold w-full py-3 text-sm"
              >
                {geoGranted ? 'Jogar →' : 'Continuar sem localização'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
